import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";

import ContentBaseFields from "components/content/ContentBaseFields";
import LifecycleActions from "components/content/LifecycleActions";
import { getContentType } from "config/contentTypeRegistry";
import { listCategories } from "api/endpoints/categories";
import {
  createFullContent,
  updateFullContent,
  getFullContent,
} from "api/endpoints/contentManagement";
import { publishContent, unpublishContent, archiveContent } from "api/endpoints/content";

const emptyBase = () => ({
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  featured_image_id: null,
  author_id: null,
  category_ids: [],
  tag_ids: [],
  seo_title: "",
  seo_description: "",
  is_gated: false,
  gating_form_id: null,
  exclude_from_sitemap: false,
  status: "draft",
  media_relations: [],
});

/**
 * Batch 2.4 — <ContentForm>. The generic create/edit form rendered inside the
 * content list's SidePanel. It composes <ContentBaseFields>, the registry's
 * type-block <Fields>, and <LifecycleActions>. One getFullContent call
 * hydrates the whole edit form.
 */
function ContentForm({ typeKey, websiteId, contentId, onSaved }) {
  const entry = getContentType(typeKey);
  const BlockFields = entry.Fields;
  const { enqueueSnackbar } = useSnackbar();

  const [base, setBase] = useState(emptyBase);
  const [block, setBlock] = useState(() => ({ ...entry.defaultBlock }));
  const [id, setId] = useState(contentId);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(Boolean(contentId));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load the website's categories (shared with the type-block fields).
  useEffect(() => {
    if (!websiteId) return;
    listCategories(websiteId)
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [websiteId]);

  // Edit hydration — one call pre-fills base + type block + ids.
  useEffect(() => {
    if (!contentId) return;
    let cancelled = false;
    setLoading(true);
    getFullContent(websiteId, entry.pathSlug, contentId)
      .then((full) => {
        if (cancelled) return;
        setBase({
          title: full.title || "",
          slug: full.slug || "",
          excerpt: full.excerpt || "",
          body: full.body || "",
          featured_image_id: full.featured_image_id || null,
          author_id: full.author_id || null,
          category_ids: full.category_ids || [],
          tag_ids: full.tag_ids || [],
          seo_title: full.seo_title || "",
          seo_description: full.seo_description || "",
          is_gated: full.is_gated || false,
          gating_form_id: full.gating_form_id || null,
          exclude_from_sitemap: full.exclude_from_sitemap || false,
          status: full.status || "draft",
          media_relations: full.media_relations || [],
        });
        setBlock(entry.fromBlockResponse(full[entry.blockKey] || {}));
        setId(full.content_id);
      })
      .catch(() => {
        if (!cancelled) enqueueSnackbar("Failed to load content", { variant: "error" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contentId, websiteId]);

  const patchBase = (patch) => setBase((prev) => ({ ...prev, ...patch }));
  const patchBlock = (patch) => setBlock((prev) => ({ ...prev, ...patch }));

  const blockContext = { categoryIds: base.category_ids, categories };

  const validate = () => {
    if (!base.title.trim()) return "Title is required.";
    if (entry.author && !base.author_id) return "An author is required.";
    if ((base.is_gated || entry.gated === "forced") && !base.gating_form_id) {
      return "A gating form is required for gated content.";
    }
    if (entry.validateBlock) return entry.validateBlock(block, blockContext);
    return null;
  };

  const buildPayload = () => {
    const payload = {
      title: base.title.trim(),
      status: base.status || "draft",
      category_ids: base.category_ids,
      tag_ids: base.tag_ids,
      [entry.blockKey]: entry.toBlockPayload(block),
    };
    if (base.slug) payload.slug = base.slug;
    if (base.excerpt) payload.excerpt = base.excerpt;
    if (base.body) payload.body = base.body;
    if (base.featured_image_id) payload.featured_image_id = base.featured_image_id;
    if (base.seo_title) payload.seo_title = base.seo_title;
    if (base.seo_description) payload.seo_description = base.seo_description;
    if (base.exclude_from_sitemap) payload.exclude_from_sitemap = true;
    if (base.is_gated || entry.gated === "forced") {
      payload.is_gated = true;
      if (base.gating_form_id) payload.gating_form_id = base.gating_form_id;
    }
    // media_relations is authored elsewhere — round-trip it unchanged on edit.
    if (id) payload.media_relations = base.media_relations;
    if (entry.author && base.author_id) payload.author_id = base.author_id;
    return payload;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      enqueueSnackbar(error, { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      if (id) {
        await updateFullContent(websiteId, entry.pathSlug, id, buildPayload());
        enqueueSnackbar("Content saved", { variant: "success" });
      } else {
        const res = await createFullContent(websiteId, entry.pathSlug, buildPayload());
        setId(res.content_id);
        enqueueSnackbar("Content created", { variant: "success" });
      }
      onSaved();
    } catch {
      enqueueSnackbar("Failed to save content", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const runLifecycle = async (action, nextStatus, label) => {
    if (!id) return;
    setBusy(true);
    try {
      await action(websiteId, id);
      patchBase({ status: nextStatus });
      enqueueSnackbar(label, { variant: "success" });
      onSaved();
    } catch {
      enqueueSnackbar(`Could not ${label.toLowerCase()}`, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </MDBox>
    );
  }

  return (
    <MDBox>
      <ContentBaseFields
        value={base}
        onChange={patchBase}
        entry={entry}
        websiteId={websiteId}
        categories={categories}
      />

      <Divider sx={{ my: 3 }} />

      <BlockFields
        value={block}
        onChange={patchBlock}
        websiteId={websiteId}
        context={blockContext}
      />

      <Divider sx={{ my: 3 }} />

      <LifecycleActions
        status={base.status}
        contentId={id}
        saving={saving}
        busy={busy}
        onSave={handleSave}
        onPublish={() => runLifecycle(publishContent, "published", "Published")}
        onUnpublish={() => runLifecycle(unpublishContent, "draft", "Unpublished")}
        onArchive={() => runLifecycle(archiveContent, "archived", "Archived")}
      />
    </MDBox>
  );
}

ContentForm.defaultProps = {
  websiteId: undefined,
  contentId: null,
};

ContentForm.propTypes = {
  typeKey: PropTypes.string.isRequired,
  websiteId: PropTypes.string,
  contentId: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
};

export default ContentForm;
